import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QuestionnaireService } from '../../services/questionnaireService';
import { Questionnaire } from '../../types/questionnaire';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import ResponsiveContainer from '../../components/ui/ResponsiveContainer';
import '../../styles/pages/QuestionnaireResultsPage.css';

interface QuestionnaireResult {
  questionnaire_id: string;
  question_id: string;
  question_text: string;
  option_id: string;
  option_text: string;
  responses: number;
}

interface QuestionSummary {
  question_id: string;
  question_text: string;
  question_type: string;
  total_responses: number;
  options: {
    option_id: string;
    option_text: string;
    responses: number;
    percentage: number;
  }[];
}

const QuestionnaireResultsPage: React.FC = () => {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [results, setResults] = useState<QuestionnaireResult[]>([]);
  const [questionSummaries, setQuestionSummaries] = useState<QuestionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalSubmissions, setTotalSubmissions] = useState(0);

  useEffect(() => {
    if (!questionnaireId || !user) {
      setError(t('questionnaireResults.missingIdOrUser'));
      setLoading(false);
      return;
    }

    fetchQuestionnaireResults();
  }, [questionnaireId, user]);

  const fetchQuestionnaireResults = async () => {
    if (!questionnaireId) return;

    try {
      setLoading(true);
      console.log('Fetching results for questionnaire:', questionnaireId);
      
      // Fetch questionnaire details
      const { data: questionnaireData, error: questionnaireError } = await QuestionnaireService.getQuestionnaireDetails(questionnaireId);
      console.log('Questionnaire details:', { data: questionnaireData, error: questionnaireError });
      if (questionnaireError) {
        console.error('Questionnaire error details:', questionnaireError);
        throw new Error(`${t('questionnaireResults.failedToLoad')}: ${(questionnaireError as any)?.message || JSON.stringify(questionnaireError)}`);
      }
      
      if (!questionnaireData) {
        throw new Error(t('questionnaireResults.notFound'));
      }
      
      // Check if user owns this questionnaire
      if (user && questionnaireData.publisher_id !== user.id) {
        throw new Error(t('questionnaireResults.notPermitted'));
      }
      
      setQuestionnaire(questionnaireData);

      // Fetch results
      const { data: resultsData, error: resultsError } = await QuestionnaireService.getQuestionnaireResults(questionnaireId);
      console.log('Questionnaire results:', { data: resultsData, error: resultsError });
      if (resultsError) throw resultsError;
      setResults(resultsData || []);

      // Process results into summaries
      processResults(resultsData || []);

      // Get total submissions
      const { data: submissionsData, error: submissionsError } = await QuestionnaireService.getSubmissionCount(questionnaireId);
      console.log('Submission count:', { data: submissionsData, error: submissionsError });
      if (submissionsError) throw submissionsError;
      setTotalSubmissions(submissionsData || 0);

    } catch (err: any) {
      console.error('Error fetching questionnaire results:', err);
      setError(err.message || t('questionnaireResults.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const processResults = (resultsData: QuestionnaireResult[]) => {
    const questionMap = new Map<string, QuestionSummary>();

    resultsData.forEach(result => {
      if (!questionMap.has(result.question_id)) {
        questionMap.set(result.question_id, {
          question_id: result.question_id,
          question_text: result.question_text,
          question_type: 'single_choice', // Will be updated from database
          total_responses: 0,
          options: []
        });
      }

      const question = questionMap.get(result.question_id)!;
      question.options.push({
        option_id: result.option_id,
        option_text: result.option_text,
        responses: result.responses,
        percentage: 0 // Will be calculated after totals
      });
      question.total_responses += result.responses;
    });

    // Calculate percentages
    questionMap.forEach(question => {
      question.options.forEach(option => {
        option.percentage = question.total_responses > 0 
          ? (option.responses / question.total_responses) * 100 
          : 0;
      });
      // Sort options by responses descending
      question.options.sort((a, b) => b.responses - a.responses);
    });

    setQuestionSummaries(Array.from(questionMap.values()));
  };

  const exportResults = () => {
    const csvData = [];
    csvData.push(['Question', 'Option', 'Responses', 'Percentage']);
    
    questionSummaries.forEach(question => {
      question.options.forEach(option => {
        csvData.push([
          question.question_text,
          option.option_text,
          option.responses.toString(),
          `${option.percentage.toFixed(1)}%`
        ]);
      });
    });

    const csvContent = csvData.map(row => row.map(field => `"${field}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `questionnaire-results-${questionnaire?.title || 'unknown'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('questionnaireResults.loading')}</span>
          </div>
          <p className="mt-2">{t('questionnaireResults.loading')}</p>
        </div>
      </ResponsiveContainer>
    );
  }

  if (error) {
    return (
      <ResponsiveContainer>
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <strong>{t('questionnaireResults.error')}</strong> {error}
        </div>
        <div className="alert alert-info">
          <strong>{t('questionnaireResults.debuggingInfo')}</strong>
          <br />{t('questionnaireResults.questionnaireId')} {questionnaireId}
          <br />{t('questionnaireResults.userId')} {user?.id}
          <br />{t('questionnaireResults.checkConsole')}
        </div>
        <button onClick={() => navigate('/questionnaires/publisher')} className="btn btn-secondary">
          <i className="bi bi-arrow-left me-2"></i>{t('questionnaireResults.backToQuestionnaires')}
        </button>
      </ResponsiveContainer>
    );
  }

  if (!questionnaire) {
    return (
      <ResponsiveContainer>
        <div className="alert alert-warning">
          <i className="bi bi-question-circle-fill me-2"></i>
          {t('questionnaireResults.notFound')}
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <div className="questionnaire-results-page">
      <ResponsiveContainer>
        {/* Header */}
        <div className="results-header">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <button onClick={() => navigate('/questionnaires/publisher')} className="btn btn-outline-secondary">
              <i className="bi bi-arrow-left me-2"></i>{t('questionnaireResults.backToQuestionnaires')}
            </button>
            <button onClick={exportResults} className="btn btn-success">
              <i className="bi bi-download me-2"></i>{t('questionnaireResults.exportCsv')}
            </button>
          </div>

          <h1 className="results-title">{questionnaire.title}</h1>
          {questionnaire.description && (
            <p className="results-description">{questionnaire.description}</p>
          )}

          <div className="results-stats">
            <div className="stat-card">
              <div className="stat-value">{totalSubmissions}</div>
              <div className="stat-label">{t('questionnaireResults.totalResponses')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{questionSummaries.length}</div>
              <div className="stat-label">{t('questionnaireResults.questions')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{questionnaire.reward_points}</div>
              <div className="stat-label">{t('questionnaireResults.pointsPerResponse')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{questionnaire.reward_points * totalSubmissions}</div>
              <div className="stat-label">{t('questionnaireResults.totalPointsAwarded')}</div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="results-content">
          {questionSummaries.length === 0 ? (
            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              {t('questionnaireResults.noResponses')}
            </div>
          ) : (
            questionSummaries.map((question, index) => (
              <div key={question.question_id} className="question-result-card">
                <div className="question-result-header">
                  <h3 className="question-result-title">
                    Q{index + 1}. {question.question_text}
                  </h3>
                  <div className="question-result-meta">
                    <span className="badge bg-primary">
                      {question.total_responses} {t('questionnaireResults.responses')}
                    </span>
                  </div>
                </div>

                <div className="options-results">
                  {question.options.map(option => (
                    <div key={option.option_id} className="option-result">
                      <div className="option-result-header">
                        <span className="option-text">{option.option_text}</span>
                        <span className="option-stats">
                          {option.responses} ({option.percentage.toFixed(1)}%)
                        </span>
                      </div>
                      <div className="option-progress">
                        <div 
                          className="option-progress-bar"
                          style={{ width: `${option.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </ResponsiveContainer>
    </div>
  );
};

export default QuestionnaireResultsPage;
